import { motion, useMotionValue } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Css } from "~generated/css";

const SET_COUNT = 3;

// A quick and dirty auto sliding carousel for our landing page
export function AutoSlidingCarousel({ images, isDesktop = false }: { images: string[]; isDesktop?: boolean }) {
  // Set dimensions based on isDesktop
  const carouselHeight = isDesktop ? 232 : 105;
  const imageMaxWidth = isDesktop ? 331 : 170;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [firstSetWidth, setFirstSetWidth] = useState(0);
  const motionValue = useMotionValue(0);

  const handleImageLoad = () => {
    setImagesLoaded((prev) => prev + 1);
  };

  // Calculate first set width once images are loaded  (images.length * 3) images = 3 sets of 4)
  useEffect(() => {
    const content = contentRef.current;
    if (!content || imagesLoaded < images.length * SET_COUNT) return;

    const calculateWidth = () => {
      let width = 0;
      const gap = 8; // gap1
      // Calculate width of first set (first images.length items)
      for (let i = 0; i < images.length; i++) {
        const child = content.children[i] as HTMLElement;
        if (child) {
          width += child.offsetWidth;
          // Add gap after each item except the last one
          if (i < images.length - 1) {
            width += gap;
          }
        }
      }
      setFirstSetWidth(width);
    };

    // Wait a bit for layout to settle
    const timeout = setTimeout(calculateWidth, 100);
    return () => clearTimeout(timeout);
  }, [imagesLoaded, images.length]);

  // Start animation once we have the width
  useEffect(() => {
    if (firstSetWidth === 0) return;

    let animationId: number;
    let position = 0;
    let lastTime: number | null = null;
    // Desktop and Mobile: 30 seconds per set
    const speed = firstSetWidth / 30000;

    const animateLoop = (currentTime: number) => {
      if (lastTime === null) {
        lastTime = currentTime;
      }

      const delta = currentTime - lastTime;
      lastTime = currentTime;

      // Continuously scroll (always negative)
      position -= speed * delta;

      // Reset when we've almost scrolled through all sets (less noticeable reset point)
      // Reset to -firstSetWidth (start of second set) which visually matches first set
      if (position <= -firstSetWidth * (SET_COUNT - 2)) {
        position = position + firstSetWidth; // Reset to -firstSetWidth
      }

      motionValue.set(position);
      animationId = requestAnimationFrame(animateLoop);
    };

    animationId = requestAnimationFrame(animateLoop);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [firstSetWidth, motionValue]);

  return (
    <div
      ref={wrapperRef}
      css={{
        ...Css.oh.hPx(carouselHeight).w100.relative.$,
      }}
    >
      <motion.div
        // This component will always trigger a diff
        data-chromatic="ignore"
        ref={contentRef}
        style={{ x: motionValue }}
        css={Css.df.gap1.w("fit-content").$}
      >
        {[...Array(SET_COUNT).fill(images)].flat().map((src, index) => (
          <img
            key={`${src}-${index}`}
            src={src}
            alt={`Carousel ${index + 1}`}
            onLoad={handleImageLoad}
            css={
              Css.h100
                .objectFit("cover")
                .wPx(imageMaxWidth)
                .ar(5 / 3).$
            }
          />
        ))}
      </motion.div>
    </div>
  );
}
